import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { listAgents, restartAgent } from '../../../services/api/agentService';
import { toast } from 'react-toastify';
import {
  Table,
  Tag,
  Button,
  Spin,
  Alert,
  Input,
  Select,
  Space,
  Typography,
  Card,
  Tooltip,
  Badge,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  PoweroffOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { getTenantsAdminList } from '../../../api/backendApi';
import { canRequestPlatformTenantList } from '../../../utils/platformTenantAccess';

const { Title, Text } = Typography;
const { Option } = Select;

const statusColors = {
  idle: 'success',
  busy: 'processing',
  error: 'error',
  offline: 'default',
  rebooting: 'warning',
  registered: 'default',
  unknown: 'default',
};

const statusIcons = {
  idle: <CheckCircleOutlined />,
  busy: <ClockCircleOutlined spin />,
  error: <ExclamationCircleOutlined />,
  offline: <StopOutlined />,
  rebooting: <PoweroffOutlined spin />,
  registered: <ClockCircleOutlined />,
  unknown: <ExclamationCircleOutlined />,
};

const AgentList = () => {
  const { userClaims, userData } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    tags: null,
    search: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isRestarting, setIsRestarting] = useState({});

  // Tenants for mapping tenant_id -> name
  const [tenants, setTenants] = useState([]);

  // For testing purposes, always enable edit rights
  const hasEditRights = true; // Override permissions check to enable actions

  // Fetch agents with current filters and pagination
  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { current, pageSize } = pagination;
      const { status, tags, search } = filters;

      const response = await listAgents({
        page: current,
        limit: pageSize,
        status,
        tags: tags ? tags.join(',') : undefined,
        search: search || undefined,
      });

      // Detailed debug logging of response
      console.log('🔍 Raw agent list API response:', response);
      console.log('🔍 Agent data structure:', response.data?.agents);

      if (Array.isArray(response.data?.agents)) {
        console.log('✅ Total agents received:', response.data.agents.length);
        if (response.data.agents.length > 0) {
          console.log('✅ First agent fields:', Object.keys(response.data.agents[0]));
          console.log('✅ First agent data:', response.data.agents[0]);
        } else {
          console.log('❌ No agents found in response');
        }
      } else {
        console.log('❌ response.data.agents is not an array:', response.data?.agents);
      }

      setAgents(response.data.agents);
      setPagination({
        ...pagination,
        total: response.data.pagination.total,
      });
    } catch (err) {
      console.error('Failed to fetch agents:', err);
      setError(err.message || 'Failed to load agents. Please try again.');
      toast.error(err.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current, pagination.pageSize]);

  // Load tenants list for mapping to names
  useEffect(() => {
    let isMounted = true;
    const loadTenants = async () => {
      try {
        const role = userClaims?.role || userData?.role || '';
        let list = [];
        if (canRequestPlatformTenantList(role)) {
          list = await getTenantsAdminList();
        }
        if (isMounted) {
          setTenants(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        console.error('Failed to load tenants:', e);
        if (isMounted) setTenants([]);
        // Non-fatal; we can still show IDs if names unavailable
      }
    };
    loadTenants();
    return () => {
      isMounted = false;
    };
  }, [userClaims?.role, userData?.role]);

  // Map of tenant id -> tenant name
  const tenantNameMap = useMemo(() => {
    const map = {};
    (tenants || []).forEach(t => {
      const id = t?.id || t?.tenantId;
      if (!id) return;
      const name = t?.name || t?.displayName || id;
      map[id] = name;
    });
    return map;
  }, [tenants]);

  // Track consecutive errors for backoff
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);

  // Fetch agents with error tracking
  const fetchAgentsWithErrorHandling = useCallback(async () => {
    try {
      await fetchAgents();
      // Reset error count on success
      if (consecutiveErrors > 0) {
        setConsecutiveErrors(0);
      }
    } catch (error) {
      // Increment error count
      setConsecutiveErrors(prev => prev + 1);
      console.warn(`Agent fetch failed ${consecutiveErrors + 1} times in a row`);
    }
  }, [fetchAgents, consecutiveErrors]);

  // Initial load and when filters/pagination change
  useEffect(() => {
    // Single on-demand fetch when dependencies (filters/pagination) change
    // Removed automatic polling to avoid repeated API usage
    fetchAgents();
  }, [fetchAgents]);

  // Handle table pagination change
  const handleTableChange = (pagination, filters) => {
    setPagination({
      ...pagination,
      current: pagination.current,
    });
  };

  // Handle agent restart
  const handleRestartAgent = async agentId => {
    if (!agentId) return;

    try {
      setIsRestarting(prev => ({ ...prev, [agentId]: true }));

      await restartAgent(agentId);

      // Refresh the agent list
      await fetchAgents();

      toast.success('Agent restart command sent successfully');
    } catch (err) {
      console.error('Failed to restart agent:', err);
      toast.error(err.message || 'Failed to restart agent');
    } finally {
      setIsRestarting(prev => ({ ...prev, [agentId]: false }));
    }
  };

  // Handle bulk actions
  const handleBulkAction = action => {
    if (selectedRowKeys.length === 0) {
      toast.warning('Please select at least one agent');
      return;
    }

    // Implement bulk actions here
    switch (action) {
      case 'restart':
        // Show confirmation for bulk restart
        if (window.confirm(`Are you sure you want to restart ${selectedRowKeys.length} agents?`)) {
          Promise.all(selectedRowKeys.map(id => handleRestartAgent(id)))
            .then(() => {
              setSelectedRowKeys([]);
              toast.success(`Restart command sent to ${selectedRowKeys.length} agents`);
            })
            .catch(err => {
              console.error('Bulk restart failed:', err);
              toast.error('Failed to restart some agents');
            });
        }
        break;
      default:
        console.warn('Unknown bulk action:', action);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Agent ID',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      render: id => (
        <Tooltip title={id}>
          <Text style={{ maxWidth: 150 }} ellipsis={{ tooltip: id }}>
            {id}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: Object.entries(statusColors).map(([value]) => ({
        text: value.charAt(0).toUpperCase() + value.slice(1),
        value,
      })),
      filterMultiple: true,
      onFilter: (value, record) => record.status === value,
      render: status => (
        <Tag
          icon={statusIcons[status] || statusIcons.unknown}
          color={statusColors[status] || 'default'}
        >
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Agent Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
      render: name => name || 'N/A',
    },
    {
      title: 'School/Building',
      dataIndex: 'school_building_name',
      key: 'school_building_name',
      width: 150,
      ellipsis: true,
      render: name => name || 'N/A',
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 120,
      ellipsis: true,
      render: tenantId => tenantNameMap[tenantId] || tenantId || 'N/A',
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: tags => (
        <>
          {tags && Array.isArray(tags)
            ? tags.map(tag => (
                <Tag color="blue" key={tag}>
                  {tag}
                </Tag>
              ))
            : 'N/A'}
        </>
      ),
    },
    {
      title: 'Last Seen',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: text => (text ? new Date(text).toLocaleString() : 'Never'),
      sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
    },
  ];

  // Row selection configuration
  const rowSelection = hasEditRights
    ? {
        selectedRowKeys,
        onChange: selectedKeys => setSelectedRowKeys(selectedKeys),
        getCheckboxProps: record => ({
          disabled: !hasEditRights,
        }),
      }
    : null;

  return (
    <div className="agent-list-container">
      <div className="agent-list-header" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Agent Management
        </Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <Space>
            <Input
              placeholder="Search agents..."
              prefix={<SearchOutlined />}
              style={{ width: 250 }}
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
            <Select
              placeholder="Filter by status"
              style={{ width: 180 }}
              allowClear
              value={filters.status || undefined}
              onChange={value => setFilters({ ...filters, status: value })}
            >
              {Object.entries(statusColors).map(([value]) => (
                <Option key={value} value={value}>
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </Option>
              ))}
            </Select>
          </Space>

          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchAgents} loading={loading}>
              Refresh
            </Button>

            {hasEditRights && selectedRowKeys.length > 0 && (
              <Select
                placeholder="Bulk Actions"
                style={{ width: 150 }}
                onChange={handleBulkAction}
                value={null}
              >
                <Option value="restart">Restart Selected</Option>
                <Option value="update_tags">Update Tags</Option>
              </Select>
            )}
          </Space>
        </div>
      </div>

      <Card className="agent-list-card" styles={{ body: { padding: 0 } }} loading={loading}>
        {error ? (
          <Alert message="Error" description={error} type="error" showIcon style={{ margin: 16 }} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={agents}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} agents`,
            }}
            loading={loading}
            onChange={handleTableChange}
            rowSelection={rowSelection}
            scroll={{ x: 'max-content' }}
            size="middle"
            rowClassName={record => `agent-row agent-status-${record.status}`}
          />
        )}
      </Card>
    </div>
  );
};

export default AgentList;
